/**
 * Spec §4 product detail content for NextOnVera, Cancer Navigator, and Grace AI.
 * Public-level "what it does / why it matters" only — no architecture, models, or infra detail.
 */

export type ProductAccent = 'cyan' | 'coral' | 'violet';

export type ProductCta = {
  label: string;
  href: string;
  variant: 'cta' | 'default' | 'outline';
};

export type ProductStep = {
  number: string;
  title: string;
  body: string;
};

export type ProductPageContent = {
  id: 'nextonvera' | 'cancer-navigator' | 'grace-ai';
  documentTitle: string;
  indexLabel: string;
  name: string;
  eyebrow: string;
  tagline: string;
  pitch: string;
  status: string;
  statusTone: 'live' | 'pilot' | 'dev';
  accent: ProductAccent;
  problemHeading: string;
  problemBody: string[];
  stepsHeading: string;
  stepsIntro?: string;
  steps: ProductStep[];
  /** Optional naming / rename note (NextOnVera). */
  namingNote?: {
    heading: string;
    body: string;
  };
  /** Optional proof / credibility block. */
  proof?: {
    heading: string;
    body: string;
  };
  /** Optional status / development callout (Cancer Navigator). */
  statusDetail?: {
    heading: string;
    body: string;
  };
  /** Optional clinical guardrail (Grace AI). */
  guardrail?: {
    heading: string;
    body: string;
  };
  /** Show illustrative Grace mockup panels (no real patient data). */
  showGraceMockups?: boolean;
  partnersNote?: string;
  cta: {
    eyebrow: string;
    heading: string;
    body: string;
    /** Optional privacy / email-handling note under the CTA body. */
    note?: string;
    primary: ProductCta;
    secondary: ProductCta;
  };
  /** Optional hero image (public path under BASE_URL). */
  heroImage?: string;
  heroImageAlt?: string;
  /** `cover` fills the text-height frame; `contain` shows the full asset. */
  heroImageFit?: 'cover' | 'contain';
  /** Renders the hero image inside a bevelled 3D metal-cast plate. */
  heroFrame?: 'platinum' | 'gold';
  /** Replace the bitmap hero with a live, sharp HTML preview. */
  heroLivePreview?: 'nextonvera-dashboard';
  backLabel: string;
  backHref: string;
};

export const nextonveraProduct: ProductPageContent = {
  id: 'nextonvera',
  documentTitle: 'NextOnVera · NextOnVera',
  indexLabel: 'Project 01',
  name: 'NextOnVera',
  eyebrow: 'Oncology intelligence',
  tagline: 'Clinical decision support, under validation, designed to reach regional teams.',
  pitch:
    "NextOnVera is a research and clinical decision-support software platform being built for oncologists, tumour boards and researchers. It is designed to assemble a patient's molecular and clinical information into a single structured profile and to present cited, structured options, including treatments, repurposing candidates and clinical trials, for the treating team to consider. It does not diagnose, determine treatment or replace clinical judgement. It is not yet available for clinical use. The validation program described below will determine whether, and how, it can be.",
  status: 'In Validation',
  statusTone: 'pilot',
  heroImage: 'generated-images/nextonvera-hero.png',
  heroImageAlt:
    'NextOnVera dashboard overview showing patient, medication, dataset and biomarker statistics',
  heroImageFit: 'contain',
  heroLivePreview: 'nextonvera-dashboard',
  accent: 'cyan',
  problemHeading: 'The problem it solves',
  problemBody: [
    "A patient's story is scattered — genomic sequencing, biomarkers, pathology, imaging and treatment history — and the literature that might match it sits in thousands of disconnected sources. A generalist or small regional team cannot realistically search all of it before the next care decision.",
    'Glioblastoma and brain cancer are the proving ground: complex, fragmented and time-critical. The architecture is pan-cancer, built to extend beyond that once the method is proven.'
  ],
  stepsHeading: 'How it works',
  stepsIntro: 'Read the person. Match the evidence. Put cited options in front of the people who decide.',
  steps: [
    {
      number: '01',
      title: 'Read',
      body:
        "NextOnVera ingests the full molecular and clinical picture into a single structured profile — genomics, biomarkers, pathology, imaging and treatment history."
    },
    {
      number: '02',
      title: 'Match',
      body:
        'AI and knowledge-graph mapping across drugs, targets, pathways and patient characteristics are designed to surface treatment, repurposing and trial options for review that a manual search may not reach.'
    },
    {
      number: '03',
      title: 'Verify',
      body:
        'Every option is graded against recognised evidence frameworks, and every citation must resolve to a real, checkable source before a clinician sees it. The output is a structured report for the treating team to consider. Nothing is automated care, and nothing is advice to a patient.'
    }
  ],
  namingNote: {
    heading: 'About the name',
    body:
      'NextOnVera was formerly known as NextCure AIx. The name changed for two reasons: “Cure” implied a guaranteed clinical outcome, which isn’t permitted for a clinical decision-support tool under Australian medical device regulation — and “AIx” conflicted with an existing registered trademark. NextOnVera combines onkos (the Greek root of “oncology”) with verus (Latin for “true” / “verified”) — the truth in oncology, verified.'
  },
  proof: {
    heading: 'What early testing showed us',
    body:
      'Working on consented data in a research setting, with no part in any patient\'s care, the prototype showed it could assemble a full molecular and clinical picture and surface options a time-poor team might otherwise miss. Whether it does that reliably, safely and across many patients is exactly what the validation program is designed to find out. Results will be published when they exist, not before.'
  },
  statusDetail: {
    heading: 'Where it stands',
    body:
      "Proof-of-concept (Phase 0) work has concluded and is under review by the program's working group. The next step is formal validation in two stages: an analytical benchmarking study using public, consented glioma data, and a blinded concordance study designed to run alongside a hospital tumour board, which will be submitted for ethics approval. Validation is being planned with leading Victorian cancer centres, and we will name partners once agreements are in place. NextOnVera is built cancer-agnostic. Glioblastoma is the proving ground."
  },
  partnersNote:
    'Partner institutions will be listed here once agreements are executed and each has approved the use of its name. Program lead: Tour de Cure.',
  cta: {
    eyebrow: 'Fund the work',
    heading: 'Fund the validation that regional teams are waiting on.',
    body:
      "NextOnVera is designed to close the gap between a tumour's full clinical picture and the options already sitting in the literature. Your support funds the studies, clinical governance and regulatory work needed to find out whether it is safe and useful, and to make it available to oncology teams, including those far from major hospitals, if it is.",
    primary: { label: 'Support the Fund', href: '/the-fund', variant: 'cta' },
    secondary: { label: 'Get involved', href: '/get-involved', variant: 'outline' }
  },
  backLabel: 'All projects',
  backHref: '/#what-we-fund'
};

export const cancerNavigatorProduct: ProductPageContent = {
  id: 'cancer-navigator',
  documentTitle: 'Cancer Navigator · NextOnVera',
  indexLabel: 'Project 03',
  name: 'Cancer Navigator',
  eyebrow: 'Orientation after diagnosis',
  tagline: 'A guided starting point when diagnosis turns the world upside down.',
  pitch:
    'A patient-and-carer experience, shaped with consumer advocates, for trusted guidance and practical next steps when life has just been rearranged. The third piece of the journey alongside NextOnVera and Grace AI.',
  status: 'In development',
  statusTone: 'dev',
  heroImage: 'generated-images/cancer-navigator-hero.png',
  heroImageAlt:
    'Cancer Navigator preview: a Tour De Cure initiative with search, diagnosis, carer, and learning starting points.',
  heroImageFit: 'contain',
  heroFrame: 'platinum',
  accent: 'violet',
  problemHeading: 'The problem it fills',
  problemBody: [
    '“It fills a gap I know personally to be significant and largely unaddressed for patients and families trying to navigate a diagnosis.” — Mark Norris',
    'A brain cancer diagnosis drops a patient and their family into an overwhelming, fragmented system overnight — different specialists, unfamiliar terminology, treatment options, financial and practical questions, and no single trusted place to start. Cancer Navigator exists to be that starting point.'
  ],
  stepsHeading: 'What we are building toward',
  stepsIntro:
    'Cancer Navigator is earlier-stage than our other funded projects. The public detail stays deliberately modest until the product is ready.',
  steps: [
    {
      number: '01',
      title: 'Orient',
      body:
        'Help newly diagnosed people and their carers find trusted information at the moment of highest confusion — without the noise of an open web search.'
    },
    {
      number: '02',
      title: 'Support both sides of care',
      body:
        'Design for patients and the people who hold them up: tools that answer practical questions families actually face between appointments.'
    },
    {
      number: '03',
      title: 'Shape with advocates',
      body:
        'Build with patient and carer consumer advocates so the portal answers real needs, not assumed ones — and keeps evolving with lived experience.'
    }
  ],
  statusDetail: {
    heading: 'Honest status',
    body:
      'Cancer Navigator is currently in development, being built in partnership with patient and carer consumer advocates. There is no public launch date and no unfinished product screenshots on this site. Register your interest to be notified when it becomes available, or get involved to help shape it.'
  },
  cta: {
    eyebrow: 'Register interest',
    heading: 'Be part of shaping Cancer Navigator.',
    body:
      'If you want early updates when Cancer Navigator is ready — or you bring lived experience that should influence it — we want to hear from you.',
    note:
      'Registering interest sends an email to Tour de Cure. We will only use your details to keep you informed about Cancer Navigator. See our Privacy Policy.',
    primary: { label: 'Register interest', href: '/get-involved', variant: 'cta' },
    secondary: { label: 'Support the Fund', href: '/the-fund', variant: 'outline' }
  },
  backLabel: 'All projects',
  backHref: '/#what-we-fund'
};

export const graceAiProduct: ProductPageContent = {
  id: 'grace-ai',
  documentTitle: 'Grace AI · NextOnVera',
  indexLabel: 'Project 02',
  name: 'Grace AI',
  eyebrow: 'Patient wellness companion',
  tagline: 'Built the opposite way to a chatbot — it asks, it does not answer.',
  pitch:
    'Grace AI is a research prototype being designed with clinicians and consumer advocates. The idea is simple: a person talks about how they are feeling, in their own words, and Grace records it as structured, timestamped check-ins that could be shared with their care team. It is not a registered medical device and is not available to patients.',
  status: 'In Validation',
  statusTone: 'live',
  accent: 'coral',
  problemHeading: 'The problem it solves',
  problemBody: [
    'Between appointments, most of a cancer journey is lived privately — fatigue, sleep, mood, pain — and limited oncology support time is spread evenly instead of toward whoever needs it most right now.',
    'Grace is designed to sit in that gap: regular check-ins that create a continuous, patient-reported record, so that care teams could see patterns earlier. Whether it does that safely and usefully is what evaluation will tell us.'
  ],
  stepsHeading: 'How it works',
  stepsIntro: 'Talk first. Structure the signal. Hand risk to a person. No diagnosis. No automated advice.',
  steps: [
    {
      number: '01',
      title: 'Check in',
      body:
        'A personal QR opens a conversation. The patient talks about how they are feeling that day — in their own words.'
    },
    {
      number: '02',
      title: 'Understand',
      body:
        'Grace structures the check-ins over time, with sentiment and trends. One mention of fatigue is a data point; a worsening pattern across check-ins is something a care team would want to see.'
    },
    {
      number: '03',
      title: 'Escalate',
      body:
        'Designed never to diagnose or advise. Any sign of a safety concern is designed to stop the conversation and hand over to a person. The aim is that care teams can see who may need attention sooner.'
    }
  ],
  guardrail: {
    heading: 'Clinical guardrail',
    body:
      'Grace AI is designed never to diagnose, treat or advise. Its purpose is to listen, structure what it hears, and help connect people to their human care team. It is designed to hand any sign of a safety concern to a person. These safeguards are being tested as part of the evaluation, not assumed.'
  },
  statusDetail: {
    heading: 'Where it stands',
    body:
      'Grace AI is a research prototype. It is being evaluated with clinicians and consumer advocates before any patient use. It is not a registered medical device and is not available to patients. The panels below are illustrations styled for this site; no real patient data is shown.'
  },
  showGraceMockups: true,
  heroImage: '/generated-images/grace-ai-hero.png',
  heroImageAlt: 'Grace AI patient check-in interface showing conversation prompts and wellness trends',
  heroImageFit: 'contain',
  heroFrame: 'gold',
  cta: {
    eyebrow: 'Fund everyday support',
    heading: 'Fund the evaluation of Grace AI',
    body:
      'If Grace AI can turn quiet daily signals into earlier human care, it will be because it was tested properly first. Supporting the Brain Cancer Fund pays for that evaluation with care teams and consumer advocates, and for the safeguards that have to be in place before any patient uses it.',
    primary: { label: 'Support the Fund', href: '/the-fund', variant: 'cta' },
    secondary: { label: 'Get involved', href: '/get-involved', variant: 'outline' }
  },
  backLabel: 'All projects',
  backHref: '/#what-we-fund'
};

export const productPages = {
  nextonvera: nextonveraProduct,
  'cancer-navigator': cancerNavigatorProduct,
  'grace-ai': graceAiProduct
} as const;
